import { useGetDownloadSignedUrlForMyAttachDocumentQuery } from '@exam-room/graphql';
import { useEffect, useState } from 'react';

import {
  getSyntheticAttachmentUrl,
  isServiceWorkerActive,
  precacheSignedAttachments,
} from '../../../services';
import { type CacheStatus } from '../types';

interface UseGetMyAttachDocumentParams {
  examCardId: string;
  refetchKey: number;
  hasAttachDocument?: boolean;
}

export function useGetMyAttachDocument(params: Readonly<UseGetMyAttachDocumentParams>) {
  const { examCardId, refetchKey, hasAttachDocument } = params;
  const [url, setUrl] = useState<string | null>(null);

  const { data, loading, error, refetch } = useGetDownloadSignedUrlForMyAttachDocumentQuery({
    variables: {
      examCardId,
    },
    skip: !examCardId || !hasAttachDocument,
  });

  useEffect(() => {
    if (!examCardId || !hasAttachDocument) {
      setUrl(null);
    }
  }, [examCardId, hasAttachDocument]);

  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    isCaching: false,
    cachedCount: 0,
    totalCount: 0,
    isComplete: false,
  });

  useEffect(() => {
    if (refetchKey > 0 && examCardId && hasAttachDocument) {
      refetch();
    }
  }, [examCardId, refetch, refetchKey, hasAttachDocument]);

  const handleCacheProgress = (payload: { count: number; total: number }) => {
    setCacheStatus((prev) => ({
      ...prev,
      cachedCount: payload.count,
      totalCount: payload.total,
    }));
  };

  useEffect(() => {
    const signedUrl = data?.erGetDownloadSignedUrlForMyAttachDocument?.signedUrl;
    if (signedUrl) {
      setUrl(signedUrl);

      if (isServiceWorkerActive()) {
        setCacheStatus(() => ({
          cachedCount: 0,
          totalCount: 0,
          isComplete: false,
          error: undefined,
          isCaching: true,
        }));

        const fileNameWithExtension = signedUrl.split('?')[0].split('/').pop() as string;
        precacheSignedAttachments([{ id: fileNameWithExtension, signedUrl }], handleCacheProgress)
          .then((res) => {
            const isComplete = res.ok && res.failed === 0;
            if (!isComplete) {
              console.error(
                `[useGetMyAttachDocument] Some attachments failed to cache: ${res.failed} out of ${res.total}`,
              );
              throw new Error(`${res.failed} out of ${res.total} attachments failed to cache`);
            }

            setCacheStatus({
              isCaching: false,
              cachedCount: res.count,
              totalCount: res.total,
              isComplete,
            });
            setUrl(getSyntheticAttachmentUrl(fileNameWithExtension));
          })
          .catch((err) => {
            console.error('[useGetMyAttachDocument] failed to cache attachment:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to cache attachment';
            setCacheStatus({
              isCaching: false,
              cachedCount: 0,
              totalCount: 1,
              isComplete: false,
              error: errorMessage,
            });
          });
      }
    }
  }, [data?.erGetDownloadSignedUrlForMyAttachDocument?.signedUrl, examCardId]);

  return {
    url,
    loading: loading || cacheStatus.isCaching,
    cacheStatus,
    error,
  };
}
